// Toast.js — Global toast notification system
// Usage: const { showToast } = useToast();
//        showToast("Saved!", "success")   // or "error" | "warning"
//
// ToastProvider wraps the app in App.js.
// ToastContainer renders at the provider level — no extra placement needed.

import React, { createContext, useContext, useState, useCallback } from "react";
import PropTypes from 'prop-types';

const ToastContext = createContext(null);

const CONFIGS = {
  success: { bg: "#22D3EE", color: "#061018", icon: "✓" },
  error:   { bg: "#DC2626", color: "#ffffff", icon: "✗" },
  warning: { bg: "#F2AB16", color: "#061018", icon: "⚠" },
};

function ToastItem({ toast, onDismiss }) {
  const cfg = CONFIGS[toast.type] || CONFIGS.success;
  return (
    <div
      onClick={() => onDismiss(toast.id)}
      style={{
        display:        "flex",
        alignItems:     "center",
        gap:            10,
        background:     cfg.bg,
        color:          cfg.color,
        fontWeight:     700,
        fontSize:       13,
        padding:        "10px 18px 10px 14px",
        borderRadius:   8,
        boxShadow:      "0 4px 16px rgba(0,0,0,0.35)",
        cursor:         "pointer",
        userSelect:     "none",
        whiteSpace:     "nowrap",
        maxWidth:       420,
        animation:      "toast-in 0.2s ease",
      }}
    >
      <span style={{ fontSize: 15, flexShrink: 0 }}>{cfg.icon}</span>
      <span style={{ flex: 1 }}>{toast.message}</span>
      <span style={{ opacity: 0.6, fontSize: 16, marginLeft: 6 }}>×</span>
    </div>
  );
}

function ToastContainer({ toasts, onDismiss }) {
  if (!toasts.length) return null;
  return (
    <>
      <style>{`
        @keyframes toast-in {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      <div
        role="status"
        aria-live="polite"
        aria-atomic="false"
        style={{
        position:       "fixed",
        bottom:         28,
        left:           "50%",
        transform:      "translateX(-50%)",
        zIndex:         9999,
        display:        "flex",
        flexDirection:  "column",
        alignItems:     "center",
        gap:            8,
        pointerEvents:  "none",
      }}>
        {toasts.map(t => (
          <div key={t.id} style={{ pointerEvents: "all" }}>
            <ToastItem toast={t} onDismiss={onDismiss} />
          </div>
        ))}
      </div>
    </>
  );
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, type = "success") => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3200);
  }, []);

  const dismiss = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

ToastProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside ToastProvider");
  return ctx;
}
